import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, ExternalLink, MapPin, Tag, Loader2, Newspaper } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';
import { SocialImage, newsCardSrc } from '@/components/SocialImage';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; }
};

const setMeta = (name, content) => {
  const isProp = name.startsWith('og:');
  let el = document.querySelector(`meta[${isProp ? 'property' : 'name'}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(isProp ? 'property' : 'name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const applyMeta = (item, pageUrl) => {
  document.title = `${item.title} | Taxera`;
  const desc = (item.summary || '').slice(0, 160);
  setMeta('description', desc);
  setMeta('og:title', item.title);
  setMeta('og:description', desc);
  setMeta('og:url', pageUrl);
  setMeta('og:type', 'article');
  setMeta('twitter:title', item.title);
  setMeta('twitter:description', desc);
  if (item.social_url) {
    setMeta('og:image', item.social_url);
    setMeta('twitter:image', item.social_url);
  }
};

const NotFound = () => (
  <div className="text-center py-20 bg-slate-50 rounded-3xl border border-slate-200" data-testid="news-detail-not-found">
    <Newspaper className="w-10 h-10 text-slate-300 mx-auto mb-4" />
    <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">News item not found</h1>
    <Link to="/news" className="text-sm text-taxera-blue font-semibold hover:underline">Back to all news</Link>
  </div>
);

const Tags = ({ item }) => (
  <div className="flex flex-wrap gap-1.5 mb-8" data-testid="news-detail-tags">
    {item.countries?.map((c) => (
      <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full">
        <MapPin className="w-3 h-3" /> {c}
      </span>
    ))}
    {item.mandate_types?.map((m) => (
      <span key={m} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full">
        <Tag className="w-3 h-3" /> {m}
      </span>
    ))}
  </div>
);

const Takeaways = ({ items }) => (
  <div className="mb-8 p-6 bg-slate-50 rounded-2xl border border-slate-100" data-testid="news-detail-takeaways">
    <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Key takeaways</div>
    <ul className="space-y-2">
      {items.map((t) => (
        <li key={t.slice(0, 40)} className="flex items-start gap-3 text-base text-slate-700 leading-relaxed">
          <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-taxera-teal mt-2.5" /> {t}
        </li>
      ))}
    </ul>
  </div>
);

const NewsArticleBody = ({ item, pageUrl }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
    <div className="flex items-center gap-3 mb-5 text-xs text-slate-400">
      <span className="font-bold text-slate-500" data-testid="news-detail-source">{item.source_name || 'Source'}</span>
      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(item.created_at)}</span>
    </div>
    <h1 className="font-heading text-3xl md:text-4xl font-bold text-taxera-charcoal leading-tight mb-5" data-testid="news-detail-title">
      {item.title}
    </h1>
    <p className="text-lg text-slate-600 leading-relaxed mb-8" data-testid="news-detail-summary">{item.summary}</p>
    {item.key_takeaways?.length > 0 && <Takeaways items={item.key_takeaways} />}
    <Tags item={item} />
    <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-slate-200">
      <ShareButtons url={pageUrl} title={item.title} summary={item.summary} />
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-taxera-blue hover:underline" data-testid="news-detail-original-link">
          Read original <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  </motion.div>
);

const NewsCardAside = ({ item, pageUrl, onMissing }) => (
  <aside className="lg:sticky lg:top-28 max-w-[320px]" data-testid="news-detail-sidebar">
    <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-2">Share this card</div>
    <SocialImage
      src={newsCardSrc(item)}
      eager
      alt={item.title}
      width={320}
      height={320}
      className="block w-full h-auto aspect-square rounded-2xl border border-slate-100 bg-slate-100 mb-3"
      testId="news-detail-social-card"
      onMissing={onMissing}
    />
    <ShareButtons url={pageUrl} title={item.title} summary={item.summary} compact />
  </aside>
);

export default function NewsArticlePage() {
  const { hash } = useParams();
  const [item, setItem] = useState(null);
  const [status, setStatus] = useState('loading');
  const [cardMissing, setCardMissing] = useState(false);
  const pageUrl = `https://taxera.tech/news/${hash}`;
  const showAside = status === 'ok' && newsCardSrc(item) && !cardMissing;

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus('loading');
    fetch(`${API}/api/news/articles/${hash}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => { setItem(data); setStatus('ok'); applyMeta(data, pageUrl); })
      .catch(() => setStatus('missing'));
  }, [hash, pageUrl]);

  return (
    <PageLayout>
      <div className={showAside ? 'max-w-6xl mx-auto px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12' : ''}>
      <article className={showAside ? 'max-w-3xl py-8 min-w-0' : 'max-w-3xl mx-auto px-6 py-8'} data-testid="news-detail">
        <Link to="/news" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-taxera-blue transition-colors mb-8" data-testid="back-to-news">
          <ArrowLeft className="w-3.5 h-3.5" /> All news
        </Link>
        {status === 'loading' && (
          <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-taxera-blue" /></div>
        )}
        {status === 'missing' && <NotFound />}
        {status === 'ok' && item && <NewsArticleBody item={item} pageUrl={pageUrl} />}
      </article>
      {showAside && (
        <div className="pb-12 lg:py-8 lg:pt-[4.5rem]">
          <NewsCardAside item={item} pageUrl={pageUrl} onMissing={() => setCardMissing(true)} />
        </div>
      )}
      </div>
    </PageLayout>
  );
}
